import { Button, Flex, notification } from 'antd';
import { getToken, isSupported, onMessage } from 'firebase/messaging';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import logo_azul from '/img/logo200x200_azul.webp';
import { APP_BASENAME } from '@/constants/urls';
import { useUiContext } from '@/contexts/UiContext';
import { useUserContext } from '@/contexts/UserContext';
import { UserNotification } from '@/model/General/Notification';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

import { messaging } from '../firebase-config';

const LOG_PREFIX = '[useFirebaseMessaging]';
const formatErrorDetails = (error: unknown) => {
  if (error instanceof DOMException) {
    return {
      kind: 'DOMException',
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack,
    };
  }

  if (error instanceof Error) {
    return {
      kind: error.constructor.name,
      name: error.name,
      message: error.message,
      stack: error.stack,
      ...(typeof (error as { code?: unknown }).code !== 'undefined'
        ? { code: (error as { code?: unknown }).code }
        : {}),
      ...(typeof (error as { cause?: unknown }).cause !== 'undefined'
        ? { cause: (error as { cause?: unknown }).cause }
        : {}),
    };
  }

  if (typeof error === 'object' && error !== null) {
    const serialized = JSON.stringify(error, null, 2);

    return { kind: 'UnknownObject', raw: serialized };
  }

  return { kind: typeof error, raw: error };
};

export interface FirebaseTokenData {
  firebase_token: string;
  device_id: string;
  device_name: string;
  user_agent: string;
  base_os: string;
  timestamp: string;
  sent: boolean;
}

export const useFirebaseMessaging = () => {
  const { t } = useTranslation();
  const { isSafari } = useUiContext();
  const { userRepository } = useRepositoriesContext();
  const { isAuthenticated, addNotification } = useUserContext();
  const [notificationIsOpen, setNotificationIsOpen] = useState(false);
  const [notificationRejected, setNotificationRejected] = useState<boolean>(() => {
    const storedValue = localStorage.getItem('notificationRejected');
    const parsedValue = storedValue ? JSON.parse(storedValue) : false;

    return parsedValue;
  });

  useEffect(() => {
    localStorage.setItem('notificationRejected', JSON.stringify(notificationRejected));
  }, [notificationRejected]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (isSafari) {
      console.log(`${LOG_PREFIX} Safari detected, registering service worker without push flow`);
      void registerServiceWorker();

      return;
    } else {
      // Para evitar errores con navegadores no soportados
      void isSupported().then(supported => {
        if (!supported) {
          console.warn(`${LOG_PREFIX} Browser is not supported for Firebase messaging`);

          return;
        }

        if ('serviceWorker' in navigator && messaging && isAuthenticated) {
          // Comprobar si las notificaciones están ya permitidas
          if (Notification.permission === 'granted') {
            void registerServiceWorkerAndGetToken();
          } else {
            // Escuchar cuando el usuario acepte los permisos
            if (
              Notification.permission === 'default' &&
              !notificationIsOpen &&
              !notificationRejected
            ) {
              const key = `open${Date.now()}`;
              const actions = (
                <Flex gap={6}>
                  <Button
                    key={'decline'}
                    style={{ color: 'black' }}
                    size="small"
                    onClick={() => {
                      notification.destroy(key);
                      setNotificationRejected(true);
                      setNotificationIsOpen(false);
                    }}
                  >
                    {t('Rechazar')}
                  </Button>
                  <Button
                    key={'ContinueButton'}
                    type="primary"
                    size="small"
                    onClick={() => {
                      notification.destroy(key);
                      setNotificationIsOpen(false);
                      void Notification.requestPermission().then(permission => {
                        if (permission === 'granted') {
                          void registerServiceWorkerAndGetToken();
                        }
                      });
                    }}
                  >
                    {t('Continuar')}
                  </Button>
                </Flex>
              );

              setNotificationIsOpen(true);
              notification.open({
                message: t('¿Querés recibir notificaciones de Unimap Web?'),
                description: `
                  ${t('Para permitir que Unimap Web te envíe notificaciones, haz click en Continuar y luego Permitir cuando el navegador de lo indique')}.`,
                actions: actions,
                key,
                duration: 0,
                closable: false,
                placement: 'bottomRight',
              });
            }
          }
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const waitForServiceWorkerRegistration = async () => {
    if (!('serviceWorker' in navigator)) {
      console.warn(`${LOG_PREFIX} Service worker not supported in this browser`);

      return null;
    }

    const currentRegistration = await navigator.serviceWorker.getRegistration();

    if (currentRegistration) {
      return currentRegistration;
    }

    try {
      return await navigator.serviceWorker.ready;
    } catch (error) {
      console.error(
        `${LOG_PREFIX} Error waiting for service worker readiness`,
        formatErrorDetails(error),
      );

      return null;
    }
  };

  const registerServiceWorker = async () => {
    try {
      if ('serviceWorker' in navigator) {
        await navigator.serviceWorker.register(`${APP_BASENAME}/firebase-messaging-sw.js`);
      }
    } catch (error) {
      console.error(`${LOG_PREFIX} Error registering service worker`, formatErrorDetails(error));
    }
  };

  const registerServiceWorkerAndGetToken = async () => {
    const maxAttempts = 5;
    let attempts = 0;
    let token: string | null = null;
    let registration: ServiceWorkerRegistration | null = null;

    while (attempts < maxAttempts && !token) {
      try {
        const reg = registration ?? (await waitForServiceWorkerRegistration());

        if (!reg || !messaging) {
          console.warn(`${LOG_PREFIX} Missing registration or messaging instance`, {
            hasRegistration: Boolean(reg),
            hasMessaging: Boolean(messaging),
          });

          registration = null;
          attempts++;
          await new Promise(resolve => setTimeout(resolve, 5000));
          continue;
        }

        registration = reg;

        // Obtener el token después de los permisos
        token = await getToken(messaging, {
          vapidKey: import.meta.env.VITE_FIREBASE_VAPID,
          serviceWorkerRegistration: reg,
        });

        if (token) {
          const firebaseToken: FirebaseTokenData = {
            firebase_token: token,
            device_id: navigator.hardwareConcurrency ? `cpu-${navigator.hardwareConcurrency}` : '-',
            device_name: '-',
            user_agent: navigator.userAgent || '-',
            base_os: '-',
            timestamp: new Date().toISOString(),
            sent: false,
          };

          userRepository.registerFirebaseToken(firebaseToken);
        } else {
          attempts++;
          await new Promise(resolve => setTimeout(resolve, 5000));
        }
      } catch (error) {
        console.warn(`${LOG_PREFIX} Error fetching token, retrying`, formatErrorDetails(error));
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
    }

    if (!token) {
      console.warn(`${LOG_PREFIX} Exhausted token attempts without success`);

      return;
    }
  };

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    /**
     * metadata:
     * - feature: firebase.messaging-listener-support-check
     * - purpose: registrar listener foreground solo si Firebase confirma soporte real del navegador
     */
    void isSupported()
      .then(supported => {
        if (cancelled || !supported || !messaging) {
          console.warn(`${LOG_PREFIX} Messaging listener skipped`, {
            supported,
            hasMessaging: Boolean(messaging),
          });

          return;
        }

        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        unsubscribe = onMessage(messaging, async message => {
          if (message.notification) {
            const newNotification: UserNotification = {
              id: message.data?.notification_id
                ? parseInt(message.data.notification_id, 10)
                : Date.now(),
              title: message.notification?.title,
              text: message.notification?.body,
              readed: false,
              is_sent: true,
              created_at: new Date().toISOString(),
              url: message.data?.url ?? null,
            };

            addNotification(newNotification);

            notification.config({ top: 72 });

            notification.open({
              message: message.notification?.title,
              description: message.notification?.body,
              icon: (
                <img
                  src={logo_azul}
                  alt="Unimap application logo in blue and white, displayed as a small icon next to notification text in a web interface. No visible text in the logo. Neutral and professional tone."
                  width={18}
                  height={18}
                />
              ),
              showProgress: true,
              onClick: () => {
                if (newNotification.url) {
                  window.open(newNotification.url, '_blank');
                }
              },
            });
          } else {
            console.log(`${LOG_PREFIX} Received message without notification payload`, message);
          }
        });
      })
      .catch(error => {
        console.warn(`${LOG_PREFIX} Messaging support check failed`, formatErrorDetails(error));
      });

    return () => {
      cancelled = true;

      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [addNotification]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event?.data?.type === 'PUSH_NOTIFICATION_RECEIVED') {
        const payload = event.data.payload as Partial<UserNotification>;
        const newNotification: UserNotification = {
          id: payload.id ?? Date.now(),
          title: payload.title ?? 'Notificación',
          text: payload.text ?? '',
          readed: false,
          is_sent: true,
          created_at: payload.created_at ?? new Date().toISOString(),
          updated_at: payload.updated_at,
          devices_info: payload.devices_info ?? null,
          readed_at: payload.readed_at ?? null,
          user: payload.user,
          url: payload.url ?? null,
        };

        addNotification(newNotification);
      }
    };

    navigator.serviceWorker?.addEventListener('message', handler);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handler);
    };
  }, [addNotification]);
};
